import { Component, useContext, useMemo, useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { RestAPI } from "@aws-amplify/api-rest";
import { withFormik, Form } from "formik";

import PromiseWrapper from "../Utils/PromiseDialog";
import LoadingDialog from "../Utils/LoadingDialog";
import SelfHelp from "../Utils/SelfHelp";
import { BlocksTable } from "./Tables";
import { callSend, AreaRow, FormRow, HeaderButtons, FormCropsSelector, Vinculos, VinculoEquipo, TituloParametros, SelectRow } from "../Form/Form";

import { updateMapInfo, GoogleMapsFarm } from "../GoogleMapsFarm/GoogleMapsFarm";
import { faInfoCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "react-toastify/dist/ReactToastify.css";

import { FarmContext } from "../../contexts/FarmContext";
import { TypesContext } from "../../contexts/TranslatedTypes";
import { applyFormula, applyFormulas } from "../../utils/area";
import getBaseLangs from "../../utils/language";
import withName from "../../hoc/withName";
import ButtonPrime from "../PrimeComponents/ButtonPrime";
import TooltipPrime from "../PrimeComponents/TooltipPrime";

const zone = "zone_form";
const AREA_FORMAT_KEY = "number.dec.4";

const getZone = (location) => {
    if (location.search.search("zone") !== -1) {
        const params = new URLSearchParams(location && location.search);
        const zone = params.get("zone");
        return zone;
    } else {
        return null;
    }
};

const getFarm = (location) => {
    const params = new URLSearchParams(location && location.search);
    const farm = params.get("farm");
    return farm;
};

const zoneTypes = (t, param) => {
    let res = "";
    let first = true;
    for (const element of param) {
        if (element === 1) {
            if (first) {
                res += t("tipo.sector.1");
                first = false;
            } else {
                res += ` - ${t("tipo.sector.1")}`;
            }
        }
        if (element === 2) {
            if (first) {
                res += t("tipo.sector.2");
                first = false;
            } else {
                res += ` - ${t("tipo.sector.2")}`;
            }
        }
        if (element === 3) {
            if (first) {
                res += t("tipo.sector.3");
                first = false;
            } else {
                res += ` - ${t("tipo.sector.3")}`;
            }
        }
    }
    return res;
};

const climaOnly = { 1: true, 2: false, 3: false };
const sueloOnly = { 1: false, 2: true, 3: false };
const riegoOnly = { 1: false, 2: false, 3: true };
// 6 is a fake type, used to represent a sector with types 2 or 3 (see FarmInfo/Tables.js -> showDDB)
const riegoSuelo = { 1: false, 2: true, 3: true, 6: true };

const allowedForType = {
    nombre_alternativo: riegoOnly,
    cultivo_especie_id: riegoSuelo,
    cultivo_variedad_id: riegoSuelo,
    sub_especie: riegoSuelo,
    kc: riegoSuelo,
    caudal_teorico: riegoOnly,
    minimo: sueloOnly,
    maximo: sueloOnly,
    punto_critico_1: sueloOnly,
    punto_critico_2: sueloOnly,
    punto_critico_3: sueloOnly,
    punto_critico_4: sueloOnly,
    calor_limite: climaOnly,
    punto_gestion_1: climaOnly,
    punto_gestion_2: climaOnly,
    helada_limite: climaOnly,
    plantas: riegoOnly,
    goteros: riegoOnly,
    plantilla_estado_fenologico_id: sueloOnly
};
const showPath = (tipos, path) => {
    const allowedTypes = tipos.length && allowedForType[path];
    return !allowedTypes || tipos.some((t) => allowedTypes[t]);
};

const OtherFarms = ({ other_farms, accountsData, t }) => {
    const navigate = useNavigate();

    const hasAccess = useMemo(() => {
        if (!other_farms || !accountsData) return {};
        const ret = {};
        other_farms.forEach((f) => {
            if (!f.campo_id) return;
            const cuenta_id = `cuenta-${f.cuenta_id}`;
            const thisAccount = accountsData.find((a) => a.id === cuenta_id);
            if (!thisAccount || !thisAccount.subChild) return;
            const campo_id = `campo-${f.campo_id}`;
            const thisCampo = thisAccount.subChild.find((f) => f.id === campo_id);
            if (thisCampo) ret[f.campo_id] = true;
        });
        return ret;
    }, [other_farms, accountsData]);
    if (!Array.isArray(other_farms)) return null;

    return other_farms.map((f) => (
        <div key={f.campo_id} className="row linea-formulario">
            <div className="col-12 mtem-05">
                <span><span className="align-middle">{t("zone.otherFarm")} </span>{hasAccess[f.campo_id] ?
                    <ButtonPrime key={f.nombre} link small label={f.nombre} onClick={() => navigate(
                        { pathname: "/farms", search: `?farm=${f.campo_id}` }
                    )}
                    />
                    : f.nombre}
                </span>
            </div>
        </div>
    ));
};

class ZoneForm extends Component {
    static contextType = TypesContext;

    getPlantillasSelector({ rolloutFenologia, plantillasFenologia, data, values, t, allowList, selectedRecords }) {
        if (!rolloutFenologia) return null;
        const categorized = [];
        // handle plantilla-selector when multiple zones are selected
        if (Array.isArray(allowList)){
            const species = selectedRecords?.map(r => r.tipo?.includes(2) && r.cultivo_especie_id);
            for (const plantilla of plantillasFenologia){
                // one category of zone specie templates
                if (species?.length > 0 && (species.includes(plantilla.cultivo_especie_id))){
                    categorized.unshift({ ...plantilla, category: t("farms.fenologia.speciePlantillas") });
                } else categorized.push({ ...plantilla, category: t("farms.fenologia.otherPlantillas") });
            }
            return categorized;
        }
        const zoneSpecie = values?.cultivo_especie_id || data?.cultivo_especie_id;
        for (const plantilla of plantillasFenologia){
            // one category of zone specie templates
            if (zoneSpecie && (plantilla.cultivo_especie_id === zoneSpecie)){
                categorized.unshift({ ...plantilla, category: t("farms.fenologia.speciePlantillas") });
            } else categorized.push({ ...plantilla, category: t("farms.fenologia.otherPlantillas") });
        }
        return categorized;
    }
    render() {
        const [types, dispatch] = this.context;
        const { resetForm, status = {}, areaInfo, canEdit, setStatus, data, values, farmId,
            t, i18n, handleSubmit, isTableDialog, allowList, alert, redirUrls, accountsData, units, equipoRelacionado, onlyDatoConsigna, hidraInfo, isPressure } = this.props;
        if (!data) return <div className="card col-xl-12 col-lg-12 col-sm-12"> <div className="card-body altura-min" /></div>;
        const showAll = !isTableDialog || !Array.isArray(allowList) || !allowList.length;
        const [lang] = i18n && getBaseLangs(i18n);
        const { cultivos: cultivosTypes } = types[lang] || {};
        if (!cultivosTypes) dispatch({ type: "get", lang, path: "cultivos" });
        const tipos = values.tipo || [];
        const isEdit = canEdit && status.edit;
        // we can edit area for irrigation sectory only if has one block
        const canEditArea = isEdit && (!showAll || !tipos.includes(3) || data.one_block);
        const showBlockWarning = isEdit && showAll && tipos.includes(3) && !data.one_block;

        const tipoSuelo = data.tipo && (data.tipo.find(a => a === 2) ? true : false);
        const tipoClima = data.tipo && (data.tipo.find(a => a === 1) ? true : false);
        const tipoRiego = data.tipo && (data.tipo.find(a => a === 3) ? true : false);
        const thisUnits = units || {};

        const selfHelpImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-zone-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-zone-1.png`];
        const selfHelpImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-zone-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-zone-2.png`];
        const selfHelpParams = {
            header: t("selfHelp.farmInfo.properties"),
            paramsGeneral: [
                t("general.name"),
                t("farms.zone.nombreAlternativo"),
                t("area.area"),
                t("cultivos.especie"),
                t("cultivos.variedad"),
                t("cultivos.subespecie"),
                t("farms.zone.kc"),
            ],
            paramsRiego: [t("farms.zone.caudalTeorico")],
            paramsSuelo: [t("farms.zone.maximo"), `${t("farms.zone.puntoCritico")} 1`, `${t("farms.zone.puntoCritico")} 2`,
                `${t("farms.zone.puntoCritico")} 3`, `${t("farms.zone.puntoCritico")} 4`, t("farms.zone.minimo")],
            paramsClima: [t("farms.zone.calorLimite"), `${t("farms.zone.puntoGestion")} 1`, `${t("farms.zone.puntoGestion")} 2`, t("farms.zone.heladaLimite")],
            definitionName: [
                t("area.area"),
                t("area.calculated"),
                t("farms.zone.kc"),

                t("farms.zone.caudalTeorico"),

                t("farms.zone.maximo"),
                t("selfHelp.definitionsNames.puntosCriticos"),
                t("farms.zone.minimo"),

                t("farms.zone.calorLimite"),
                t("selfHelp.definitionsNames.temperaturas"),
                t("farms.zone.heladaLimite")
            ],
            definitionText: [
                t("selfHelp.definitions.area"),
                t("selfHelp.definitions.estimatedArea"),
                t("selfHelp.definitions.kc"),
                t("selfHelp.definitions.caudalTeorico"),
                t("selfHelp.definitions.maximo"),
                t("selfHelp.definitions.puntosCriticos"),
                t("selfHelp.definitions.minimo"),
                t("selfHelp.definitions.calor"),
                t("selfHelp.definitions.temperaturas"),
                t("selfHelp.definitions.heladas"),

            ]
        };
        this.plantillasSelector = this.getPlantillasSelector(this.props);
        return (
            <div className={isTableDialog ? "" : "card"}>
                <div className={isTableDialog ? "" : "card-body altura-min"}>
                    <Form id={zone} onSubmit={handleSubmit}>
                        {!isTableDialog ? <>
                            <div className='row' style={{ alignItems: "center" }}>
                                <div className='col-3'>
                                    <SelfHelp t={t} params={selfHelpParams} images={[selfHelpImg1, selfHelpImg2]} lng={i18n.language} zoneForm />
                                </div>
                                <div className='col-6 text-center'>
                                    <h5 className="card-title"><span>{t("farms.zone.zoneInfo")}</span>
                                    </h5>
                                </div>
                                <div className='col-3 d-flex justify-content-end align-items-center'>
                                    <HeaderButtons edit={status.edit} canEdit={canEdit} setStatus={setStatus} resetForm={resetForm} form={zone} />
                                </div>
                            </div>
                            <div className='linea-titulo' />
                        </> : alert ? <div className="alert alert-warning" role="alert">{t(alert)}</div>
                            : null}
                        <div className='row'>
                            {onlyDatoConsigna ?
                                <div className='col-12 text-center'>
                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number", { value: isPressure?data.setpoint:data.porcentaje })}
                                        step1={isPressure?"0.01":"0"}
                                        min1={0}
                                        max1={isPressure?null:100}
                                        nombre1={t("farms.zone.datoConsigna")}
                                        campo1={isPressure?"setpoint":"porcentaje"}
                                        unit1 = {isPressure?"bar":"%"}
                                        type1="number"
                                    />
                                </div>
                                :<div className='col-12 text-center'>
                                    <FormRow
                                        edit={isEdit}
                                        dato1={data.nombre}
                                        nombre1={isEdit ? `${t("general.name")} *` : t("general.name")}
                                        campo1={(showAll || allowList.includes("nombre")) && "nombre"}
                                    />
                                    <FormRow
                                        edit={isEdit}
                                        dato1={data.nombre_alternativo}
                                        nombre1={t("farms.zone.nombreAlternativo")}
                                        campo1={showPath(tipos, "cultivo_especie_id") && (showAll || allowList.includes("nombre_alternativo")) && "nombre_alternativo"}
                                    />
                                    <FormRow
                                        dato1={(showAll || allowList.includes("tipo")) && zoneTypes(t, data.tipo)}
                                        nombre1={t("general.type")}
                                        campo1="tipo"
                                    />
                                    <AreaRow
                                        edit={canEditArea}
                                        warning={showBlockWarning && t("zone.modifyBlocks")}
                                        dato1={t("general.number_unit", { value: { number: data.superficie, unit: areaInfo.unidad } })}
                                        dato2={areaInfo.calculated && t("general.number_unit", { value: { number: areaInfo.calculated, unit: areaInfo.unidad } })}
                                        nombre1={t("area.area")}
                                        nombre2={t("area.calculated")}
                                        campo1={(showAll || allowList.includes("superficie")) && "superficie"}
                                        unit1={areaInfo.unidad}
                                        unit2={areaInfo.unidad}
                                    />
                                    {showPath(tipos, "cultivo_especie_id") && (showAll || allowList.includes("cultivo_especie_id")) ?
                                        <FormCropsSelector
                                            cultivos={cultivosTypes}
                                            sector={data}
                                            especieId={values.cultivo_especie_id || data.cultivo_especie_id}
                                            variedadId={values.cultivo_variedad_id || data.cultivo_variedad_id}
                                            edit={isEdit}
                                        /> : null}

                                    <FormRow
                                        edit={isEdit}
                                        dato2={t("general.number", { value: data.kc })}
                                        step2="0.01"
                                        nombre2={t("farms.zone.kc")}
                                        campo2={showPath(tipos, "kc") && (showAll || allowList.includes("kc")) && "kc"}
                                        type2="number"
                                    />

                                    {this.plantillasSelector && showPath(tipos, "plantilla_estado_fenologico_id") && (showAll || allowList.includes("plantilla_estado_fenologico_id")) ?
                                        <SelectRow
                                            edit={isEdit}
                                            nombre={t("farms.fenologia.plantilla")}
                                            dataValue={data.plantilla_estado_fenologico_id}
                                            campo="plantilla_estado_fenologico_id"
                                            options={this.plantillasSelector}
                                            optionKey="plantilla_estado_fenologico_id"
                                            optionLabel="nombre"
                                            groupBy="category"
                                            placeholder={t("farms.fenologia.selectPlantilla")}
                                            showClearButton
                                        />
                                        : null}

                                    {!isTableDialog && <Vinculos zoneDC={redirUrls} />}
                                    {!isTableDialog && data.other_farms && <OtherFarms other_farms={data.other_farms} accountsData={accountsData} t={t} />}

                                    {data.caudal_teorico && tipoRiego ?
                                        <TituloParametros titulo={t("forms.tituloParametrosRiego")} />
                                        :
                                        isEdit && tipoRiego &&
                  <TituloParametros titulo={t("forms.tituloParametrosRiego")} />}
                                    <FormRow
                                        // can edit area also includes sectors that have not "type 3", however showPath handles that case
                                        edit={canEditArea}
                                        dato1={t("general.number", { value: data.plantas })}
                                        nombre1={t("blocks.totalPlantas")}
                                        campo1={showPath(tipos, "plantas") && (showAll || allowList.includes("plantas")) && "plantas"}
                                        type1="number"
                                        dato2={t("general.number", { value: data.goteros })}
                                        nombre2={t("blocks.totalGoteros")}
                                        campo2={showPath(tipos, "goteros") && (showAll || allowList.includes("goteros")) && "goteros"}
                                        type2="number"
                                    />

                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number_unit", { value: { number: data.caudal_teorico, unit: thisUnits.cteorico_unidad } })}
                                        nombre1={isEdit ? `${t("farms.zone.caudalTeorico")} *` : t("farms.zone.caudalTeorico")}
                                        campo1={showPath(tipos, "caudal_teorico") && (showAll || allowList.includes("caudal_teorico")) && "caudal_teorico"}
                                        type1="number"
                                        unit1={thisUnits.cteorico_unidad}
                                    />

                                    {data.one_block ?
                                        <FormRow
                                            edit={isEdit}
                                            dato1={data.ano_plantacion}
                                            nombre1={t("blocks.anioPlantacion")}
                                            campo1={showPath(tipos, "ano_plantacion") && (showAll || allowList.includes("ano_plantacion")) && "ano_plantacion"}
                                            type1="number"
                                        />
                                        : null}

                                    {equipoRelacionado &&
                  <VinculoEquipo farmId={farmId} equipo_id={data.equipo[0]} equipoRelacionado={equipoRelacionado.nombre} t={t} />}

                                    {(data.maximo || data.minimo ||
                  data.punto_critico_1 || data.punto_critico_2 ||
                  data.punto_critico_3 || data.punto_critico_4 || data.ano_plantacion) && tipoSuelo ?
                                        <TituloParametros titulo={t("forms.tituloParametrosSuelo")} />
                                        :
                                        isEdit && tipoSuelo &&
                  <TituloParametros titulo={t("forms.tituloParametrosSuelo")} />}

                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number", { value: data.maximo })}
                                        nombre1={t("farms.zone.maximo")}
                                        campo1={showPath(tipos, "maximo") && (showAll || allowList.includes("maximo")) && "maximo"}
                                        type1="number"
                                        dato2={t("general.number", { value: data.punto_critico_1 })}
                                        nombre2={`${t("farms.zone.puntoCritico")} 1`}
                                        campo2={showPath(tipos, "punto_critico_1") && (showAll || allowList.includes("punto_critico_1")) && "punto_critico_1"}
                                        type2="number"
                                    />
                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number", { value: data.punto_critico_2 })}
                                        nombre1={`${t("farms.zone.puntoCritico")} 2`}
                                        campo1={showPath(tipos, "punto_critico_2") && (showAll || allowList.includes("punto_critico_2")) && "punto_critico_2"}
                                        type1="number"
                                        dato2={t("general.number", { value: data.punto_critico_3 })}
                                        nombre2={`${t("farms.zone.puntoCritico")} 3`}
                                        campo2={showPath(tipos, "punto_critico_3") && (showAll || allowList.includes("punto_critico_3")) && "punto_critico_3"}
                                        type2="number"
                                    />
                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number", { value: data.punto_critico_4 })}
                                        nombre1={`${t("farms.zone.puntoCritico")} 4`}
                                        campo1={showPath(tipos, "punto_critico_4") && (showAll || allowList.includes("punto_critico_4")) && "punto_critico_4"}
                                        type1="number"
                                        dato2={t("general.number", { value: data.minimo })}
                                        nombre2={t("farms.zone.minimo")}
                                        campo2={showPath(tipos, "minimo") && (showAll || allowList.includes("minimo")) && "minimo"}
                                        type2="number"
                                    />

                                    {(data.calor_limite || data.punto_gestion_1 || data.punto_gestion_2 || data.helada_limite) && tipoClima ?
                                        <TituloParametros titulo={t("forms.tituloParametrosClima")} />
                                        :
                                        isEdit && tipoClima &&
                  <TituloParametros titulo={t("forms.tituloParametrosClima")} />}
                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number_unit", { value: { number: data.calor_limite, unit: thisUnits.temp_unidad } })}
                                        nombre1={t("farms.zone.calorLimite")}
                                        campo1={showPath(tipos, "calor_limite") && (showAll || allowList.includes("calor_limite")) && "calor_limite"}
                                        type1="number"
                                        unit1={thisUnits.temp_unidad}
                                        dato2={t("general.number_unit", { value: { number: data.punto_gestion_1, unit: thisUnits.temp_unidad } })}
                                        nombre2={`${t("farms.zone.puntoGestion")} 1 `}
                                        campo2={showPath(tipos, "punto_gestion_1") && (showAll || allowList.includes("punto_gestion_1")) && "punto_gestion_1"}
                                        type2="number"
                                        unit2={thisUnits.temp_unidad}
                                    />
                                    <FormRow
                                        edit={isEdit}
                                        dato1={t("general.number_unit", { value: { number: data.punto_gestion_2, unit: thisUnits.temp_unidad } })}
                                        nombre1={`${t("farms.zone.puntoGestion")} 2 `}
                                        campo1={showPath(tipos, "punto_gestion_2") && (showAll || allowList.includes("punto_gestion_2")) && "punto_gestion_2"}
                                        type1="number"
                                        unit1={thisUnits.temp_unidad}
                                        dato2={t("general.number_unit", { value: { number: data.helada_limite, unit: thisUnits.temp_unidad } })}
                                        nombre2={t("farms.zone.heladaLimite")}
                                        campo2={showPath(tipos, "helada_limite") && (showAll || allowList.includes("helada_limite")) && "helada_limite"}
                                        type2="number"
                                        unit2={thisUnits.temp_unidad}
                                    />

                                    {!showAll && allowList.includes("superficie_dividida") &&
                  <FormRow
                      edit={isEdit}
                      dato1={values.superficie_dividida}
                      nombre1={t("area.divided")}
                      campo1="superficie_dividida"
                      type1="checkbox"
                  />}

                                </div>}
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}

const FormikForm = withFormik({
    mapPropsToStatus: (props) => {
        return {
            edit: (props && props.edit) || false,
        };
    },
    mapPropsToValues: (props) => {
        const data = Object.assign({}, props.data);
        const hidraInfo = Object.assign({}, props.hidraInfo);
        if (props.onlyDatoConsigna && data && data.grupo_variador_id) {
            return {
                [data.tipo_setpoint==="PRESSURE"?"setpoint":"porcentaje"]: data.tipo_setpoint==="PRESSURE"?data.setpoint:data.porcentaje,
                tipo_setpoint: data.tipo_setpoint??"PERCENTAGE",
                grupo_variador_id: data.grupo_variador_id,
                variador_frecuencia_id: hidraInfo.variador_frecuencia_id
            };
        } else {
            const tipos = (Array.isArray(data.tipo) && data.tipo.length) ? data.tipo : [];
            return {
                tipo: tipos,
                nombre: data.nombre,
                superficie: data.superficie,
                nombre_alternativo: showPath(tipos, "nombre_alternativo") ? data.nombre_alternativo : undefined,
                cultivo_especie_id: showPath(tipos, "cultivo_especie_id") ? data.cultivo_especie_id : undefined,
                cultivo_variedad_id: showPath(tipos, "cultivo_variedad_id") ? data.cultivo_variedad_id : undefined,
                sub_especie: showPath(tipos, "sub_especie") ? data.sub_especie : undefined,
                caudal_teorico: showPath(tipos, "caudal_teorico") ? data.caudal_teorico : undefined,
                kc: showPath(tipos, "kc") ? data.kc : undefined,
                maximo: showPath(tipos, "maximo") ? data.maximo : undefined,
                punto_critico_1: showPath(tipos, "punto_critico_1") ? data.punto_critico_1 : undefined,
                punto_critico_2: showPath(tipos, "punto_critico_2") ? data.punto_critico_2 : undefined,
                punto_critico_3: showPath(tipos, "punto_critico_3") ? data.punto_critico_3 : undefined,
                punto_critico_4: showPath(tipos, "punto_critico_4") ? data.punto_critico_4 : undefined,
                minimo: showPath(tipos, "minimo") ? data.minimo : undefined,
                calor_limite: showPath(tipos, "calor_limite") ? data.calor_limite : undefined,
                punto_gestion_1: showPath(tipos, "punto_gestion_1") ? data.punto_gestion_1 : undefined,
                punto_gestion_2: showPath(tipos, "punto_gestion_2") ? data.punto_gestion_2 : undefined,
                helada_limite: showPath(tipos, "helada_limite") ? data.helada_limite : undefined,
                plantas: showPath(tipos, "plantas") ? data.plantas : undefined,
                goteros: showPath(tipos, "goteros") ? data.goteros : undefined,
                tipo_setpoint: props.isPressure?"PRESSURE":"PERCENTAGE",
                superficie_dividida: false,
                ano_plantacion: showPath(tipos, "ano_plantacion") ? data.ano_plantacion : undefined,
                plantilla_estado_fenologico_id: showPath(tipos, "plantilla_estado_fenologico_id") ? data.plantilla_estado_fenologico_id : undefined
            };
        }
    },
    enableReinitialize: true,
    handleSubmit: (values, { props, ...actions }) => {
        props.updateData({...values, sector_id:props.data.sector_id}, props.data, "sector_id");
        if (!props.cancel) actions.setStatus({ edit: false });
    }
})(withTranslation()(ZoneForm));

const Zone = ({ dataFarm, mapRef, accountsData }) => {
    const [_, dispatch] = useContext(FarmContext);
    const location = useLocation();
    const zoneId = getZone(location);
    const farmId = getFarm(location);
    const { t, i18n } = useTranslation();
    const [urls, setUrls] = useState({});
    const promiseWrapper = useRef(null);
    const closeEstadosRef = useRef(null);
    const units = dispatch({ type: "get", path: "units" });
    const [info, setInfo] = useState({ data: null, isLoaded: false });
    const areaInfo = useRef({});
    const [edit, setEdit] = useState(false);
    const canEdit = dispatch({ type: "get", path: "canEdit" });
    const equiposDict = dispatch({ type: "get", path: "pumpsysDict" });

    const selfHelpImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-zonemap-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-zonemap-1.png`];
    const selfHelpImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-zonemap-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-zonemap-2.png`];
    const selfHelpImg3 = import.meta.glob("../../img/autoayuda/*/autoayuda-zonemap-3.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-zonemap-3.png`];

    const blockSimpleImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-blocks-simple-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-blocks-simple-1.png`];
    const blockSimpleImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-blocks-simple-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-blocks-simple-2.png`];

    const blockMassImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-blocks-mass-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-blocks-mass-1.png`];
    const blockMassImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-blocks-mass-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-blocks-mass-2.png`];
    const blockMassImg3 = import.meta.glob("../../img/autoayuda/*/autoayuda-blocks-mass-3.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-blocks-mass-3.png`];
    const blockMassImg4 = import.meta.glob("../../img/autoayuda/*/autoayuda-blocks-mass-4.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-blocks-mass-4.png`];

    const visibleMap = useMemo(() => {
        if (dataFarm.mapa_json && dataFarm.mapa_json.configurado) return true;
        return false;
    }, [dataFarm]);

    useEffect(() => {
        setInfo((state) => {
            return { ...state, isLoaded: false };
        });
        if (edit) setEdit(false);
        if (zoneId !== null) {
            const param = `/farms/${farmId}/sector/${zoneId}`;
            const getDatos = async () => {
                RestAPI.get("apiAdminDropControl", param)
                    .then(res => JSON.parse(JSON.stringify(res)))
                    .then(([data]) => {
                        const { formula, formulaTo } = areaInfo.current;
                        areaInfo.current.apiArea = data && data.superficie;
                        const hasAreaData = formula && formulaTo;
                        if (hasAreaData && data && data.superficie) {
                            // we can use formula2( formula1( sup / 10.000 ) )
                            // instead of formula2( formula1( sup ) / 10.000 ) for area
                            data.superficie = applyFormulas(data.superficie / 10000, formulaTo, formula, AREA_FORMAT_KEY);
                        }

                        if (data.one_block) {
                            const plantacion = Array.isArray(data.bloques) && data.bloques[0].year_plantacion || data.ano_plantacion || null;
                            data.ano_plantacion = plantacion;
                        }

                        setInfo({ isLoaded: true, data });
                        // if we don't have the area info, it means we still dont have farmdata, so we shouldn't update it
                        hasAreaData && data && dispatch({ type: "update", path: "sectors", params: [data], skipReload: true });
                    })
                    .catch(err => console.error(err));
            };
            getDatos();
        }
    // this runs on initial zone-page load, so only depends on the id
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoneId]);

    useEffect(() => {
        const { formula_from_kilo_base: formula, formula_to_base: formulaTo, kilo_unidad,
            formula_from_base: formulaInv, formula_to_kilo_base: formulaInvTo } = dataFarm && dataFarm.areaInfo;
        const unidad = kilo_unidad || "";

        const i = areaInfo.current;
        // we don't need to re-convert data from the server
        if (i.formula === formula && i.unidad === unidad && i.formulaTo === formulaTo) {
            if (i.formulaInv !== formulaInv || i.formulaInvTo !== formulaInvTo) {
                areaInfo.current = { ...i, formulaInv, formulaInvTo };
            }
            return;
        }
        const ret = { ...i, formula, formulaTo, formulaInv, formulaInvTo, unidad };
        if (ret.baseArea) {
            ret.calculated = applyFormula(ret.baseArea / 10000, formula);
        }
        areaInfo.current = ret;

        setInfo((info) => {
            if (ret.apiArea) {
                const sector = info.data;
                if (sector && sector.superficie) {
                    sector.superficie = applyFormulas(ret.apiArea / 10000, formulaTo, formula, AREA_FORMAT_KEY);
                }
            }
            return { ...info };
        });
    }, [dataFarm]);

    const { filterSectors, updateSectorArea } = useMemo(() => ({
        filterSectors: [zoneId],
        updateSectorArea: (id, area) => {
            const i = areaInfo.current;
            if (id !== zoneId || i.baseArea === area) return;
            const { formula } = i;
            areaInfo.current = { ...i, baseArea: area, calculated: area && applyFormula(area / 10000, formula) };
            setInfo((info) => ({ ...info })); // force re-render
        }
    }), [zoneId]);

    const dataSector = dataFarm.mapa_json && dataFarm.mapa_json.sectores && dataFarm.mapa_json.sectores[zoneId];
    const tooltipVisible = (!edit && (!dataSector || !Array.isArray(dataSector.path) || dataSector.path.length < 2)) ? true : false;

    useEffect(() => {
        const accountId = dataFarm.cuenta_id && dataFarm.cuenta_id;
        const newUrls = { clima: null, suelo: null, riego: null };

        if (info.data) {
            const commonQuery = `cuentaId=${accountId}&campoId=${farmId}&sectorId=${zoneId}`;
            info.data.tipo.forEach(tipo => {
                if (tipo === 1) { //clima
                    newUrls.clima = `https://www.dropcontrol.com/site/ha/clima.do?${commonQuery}`;
                }
                if (tipo === 2) { //suelo
                    newUrls.suelo = `https://www.dropcontrol.com/site/ha/suelo.do?${commonQuery}`;
                }
                if (tipo === 3) { //riego
                    newUrls.riego = `https://www.dropcontrol.com/site/ha/riego.do?${commonQuery}`;
                }
            });
        }
        setUrls(newUrls);
    // info.data is always updated after farmId/zoneId
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info.data, dataFarm]);

    const blocksData = info.data && info.data.bloques;

    const equipoRelacionado = useMemo(() => {
        if (info.data && info.data.tipo.includes(3)) {
            if (equiposDict !== undefined && equiposDict !== null) {
                const equipo = info.data && equiposDict[info.data.equipo[0]];
                return equipo;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }, [equiposDict, info]);

    return (
        <div className='row-container'>
            {info.isLoaded === false && <LoadingDialog />}
            <div className='row'>
                <div className="col-xl-6 col-lg-12 col-sm-12">
                    <PromiseWrapper
                        ref={promiseWrapper}
                        yesText={t("general.answerYes")} noText={t("general.answerNo")}
                        headerText={t("zone.recalcHeader")} contentText={t("zone.recalcBody")}
                    />
                    <PromiseWrapper
                        ref={closeEstadosRef}
                        yesText={t("forms.save")} noText={t("forms.cancel")}
                        headerText={t("zone.closeEstadosHeader")} contentText={t("zone.closeEstadosBody")}
                    />
                    <FormikForm
                        canEdit={canEdit}
                        farmId={farmId}
                        data={info.data}
                        redirUrls={urls}
                        areaInfo={areaInfo.current}
                        units={units}
                        accountsData={accountsData}
                        equipoRelacionado={equipoRelacionado}
                        rolloutFenologia={dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado}
                        plantillasFenologia={dataFarm?.fenologia}
                        updateData={(newData, props, actions) => {
                            callSend(`/farms/${farmId}/sector/${zoneId}`, info.data, newData, t, (args) => {
                                setInfo(args);
                                dispatch({ type: "update", path: "sectors", params: [args.data] });
                            }, false, areaInfo.current, promiseWrapper.current, undefined, farmId, closeEstadosRef.current, actions, true);
                        }}
                    />
                </div>

                <div className="col-xl-6 col-lg-12 col-sm-12 map-col">
                    <div className="card">
                        <div className="card-body">
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-4'>
                                    <SelfHelp t={t} params={null} images={[selfHelpImg1, selfHelpImg2, selfHelpImg3]} lng={i18n.language} zoneMap />
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                    <h5 className="card-title">
                                        <span>{t("maps.map")}</span>
                                        {tooltipVisible ?
                                            <TooltipPrime id="zone-map-nodata"  content={t("maps.nodata")} position="bottom" >
                                                <FontAwesomeIcon size="lg" icon={faInfoCircle} style={{ color: "tomato", fontSize:"1.25rem" }} />
                                            </TooltipPrime>
                                            : null}
                                    </h5>
                                </div>
                                <div className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center'>
                                    {!visibleMap && edit &&
                      <TooltipPrime id="zone-map-no-visible-tip"  content={t("maps.mapaNoVisibleTip")} position="bottom" >
                          <FontAwesomeIcon size="1x" style={{ color: "orange", marginRight:"20px" }} icon={faExclamationTriangle} />
                      </TooltipPrime>}
                                    <form id="zone_map" onSubmit={(e) => {
                                        e.preventDefault();
                                        if (mapRef.current) {
                                            const values = mapRef.current.getMapInfo({ sectorId: zoneId });
                                            updateMapInfo(farmId, values, t, dispatch, !visibleMap);
                                            setEdit(false);
                                        }
                                    }}
                                    >
                                        <HeaderButtons form="zone_map" edit={edit} canEdit={canEdit} setEdit={setEdit} mapRef={mapRef.current} mapOptions={dataFarm.mapa_json} resetForm={() => null} />
                                    </form>
                                </div>
                            </div>
                            <div className='linea-titulo' />
                            <div className='mapsWrapper'>
                                <GoogleMapsFarm
                                    apiKey="AIzaSyCvs7xPDh2vfmQO2w02lcItOXa6OKOVmQ4"
                                    data={dataFarm.mapa_json}
                                    edit={canEdit && edit}
                                    farmId={dataFarm.campo_id}
                                    filterSectors={filterSectors}
                                    hideNodes
                                    husoHorario={dataFarm.huso_horario}
                                    innerRef={mapRef}
                                    language={i18n.language}
                                    updateSectorArea={updateSectorArea}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {(Array.isArray(blocksData) && blocksData.length > 1) ?
                <div className='row'>
                    <div className="col">
                        <div className="card">
                            <div className="card-body">
                                <div className='row'>
                                    <div className='col col-sm-2 col-md-3 col-lg-4'>
                                        <SelfHelp
                                            t={t}
                                            params={null}
                                            images={{ simple: [blockSimpleImg1, blockSimpleImg2], mass: [blockMassImg1, blockMassImg2, blockMassImg3, blockMassImg4] }}
                                            lng={i18n.language}
                                            blockTable
                                        />
                                    </div>
                                    <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                        <h5 className="card-title"><span>{t("blocks.blocks")}</span></h5>
                                    </div>
                                    <div id="blocksTableToolbar"
                                        className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center'
                                    />
                                </div>
                                <div className='linea-titulo' />
                                <BlocksTable dataFarm={dataFarm} blocksInfo={blocksData} dispatch={({ type, path, params }) => {
                                    if (type !== "update" || path !== "blocks" || !Array.isArray(params)) return;
                                    let obj = undefined;
                                    const sector_id = parseInt(zoneId, 10);
                                    params.forEach((newData) => {
                                        const bloqueId = newData.bloque_id;
                                        obj = blocksData.find((oldData) => oldData.bloque_id === bloqueId);
                                        if (!obj) return;

                                        if (newData.sectors) {
                                            const { formula, formulaTo } = areaInfo.current;
                                            newData.sectors.forEach(s => {
                                                if (s.superficie !== undefined) s.apiArea = s.superficie;
                                                if (s.superficie && formula && formulaTo) {
                                                    s.superficie = applyFormulas(s.superficie / 10000, formulaTo, formula, AREA_FORMAT_KEY);
                                                }
                                                if (s.sector_id === sector_id) setInfo((current) => {
                                                    Object.assign(current.data, s);
                                                    return current;
                                                });
                                            });
                                            dispatch({ type: "update", path: "sectors", params: newData.sectors });
                                            delete newData.sectors;
                                        }
                                        Object.assign(obj, newData);
                                    });
                                    return blocksData;
                                }} sector_id={zoneId} canEdit={canEdit} t={t}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                : null}
        </div>
    );
};

export { showPath, FormikForm as ZoneForm };
export default withName(Zone, "ZoneFrag");
